import type { WakeProgram, WakeState } from './types.js'

/**
 * Движок таймера. Порт `shared/.../WakeEngine.kt` один в один: те же формулы,
 * те же границы, те же значения на краях. Все функции чистые, `now` — миллисекунды epoch.
 */

export function totalMinutes(program: WakeProgram): number {
  return program.phases.reduce((sum, p) => sum + p.minutes, 0)
}

/** Сколько минут занимают фазы до [index]. */
export function minutesBefore(program: WakeProgram, index: number): number {
  return program.phases.slice(0, index).reduce((sum, p) => sum + p.minutes, 0)
}

export function isRunning(state: WakeState): boolean {
  return state.windowStartMillis !== null
}

export function elapsedMinutes(state: WakeState, now: number): number {
  return state.windowStartMillis === null ? 0 : (now - state.windowStartMillis) / 60000
}

/**
 * −1 — окно не запущено. После плановой границы остаётся последняя фаза: по времени окно
 * не закрывается, закрывает его только ответ человека на «Уснул?» (правка 26.09.2026).
 * Раньше по истечении плана движок сам объявлял окно закрытым — то есть решал за человека.
 */
export function currentIndex(state: WakeState, now: number): number {
  const start = state.windowStartMillis
  if (start === null) return -1
  const phases = state.program.phases
  const elapsed = (now - start) / 60000
  let acc = 0
  for (let i = 0; i < phases.length; i++) {
    const phase = phases[i]
    if (phase === undefined) break
    if (elapsed < acc + phase.minutes) return i
    acc += phase.minutes
  }
  return phases.length - 1
}

/** Через сколько окно без ответа считается брошенным: два часа после плана (решение 26.09.2026). */
export const ABANDON_AFTER_SECONDS = 2 * 3600

/**
 * Окно брошено: план давно вышел, а на «Уснул?» так и не ответили. Держать его открытым
 * дальше незачем — следующее «Проснулся» должно лечь чисто. Это не «уснул за него»:
 * в журнал такое окно уходит с причиной «без ответа».
 */
export function isAbandoned(state: WakeState, now: number): boolean {
  return overtimeSeconds(state, now) >= ABANDON_AFTER_SECONDS
}

/** Сколько секунд последняя фаза идёт сверх плана; 0, пока план не вышел. */
export function overtimeSeconds(state: WakeState, now: number): number {
  const end = windowEndMillis(state)
  return end === null ? 0 : Math.max(0, Math.floor((now - end) / 1000))
}

export function secondsLeftInPhase(state: WakeState, now: number): number {
  const i = currentIndex(state, now)
  const phase = state.program.phases[i]
  if (i < 0 || phase === undefined || state.windowStartMillis === null) return 0
  const endMillis = state.windowStartMillis + (minutesBefore(state.program, i) + phase.minutes) * 60_000
  return Math.max(0, Math.floor((endMillis - now) / 1000))
}

export function secondsToSleep(state: WakeState, now: number): number {
  const start = state.windowStartMillis
  if (start === null) return 0
  return Math.max(0, Math.floor((start + totalMinutes(state.program) * 60_000 - now) / 1000))
}

/** Момент начала фазы [index] — для будильников и уведомлений. */
export function phaseStartMillis(state: WakeState, index: number): number | null {
  return state.windowStartMillis === null ? null : state.windowStartMillis + minutesBefore(state.program, index) * 60_000
}

export function windowEndMillis(state: WakeState): number | null {
  return state.windowStartMillis === null ? null : state.windowStartMillis + totalMinutes(state.program) * 60_000
}

export function start(state: WakeState, now: number): WakeState {
  return { ...state, windowStartMillis: now }
}

/**
 * Перейти к фазе [index]: начало окна сдвигается назад так, будто предыдущие фазы
 * прошли ровно по расписанию. Индекс ограничивается диапазоном `0..phases.length - 1`.
 */
export function goTo(state: WakeState, index: number, now: number): WakeState {
  const i = Math.min(Math.max(index, 0), state.program.phases.length - 1)
  return { ...state, windowStartMillis: now - minutesBefore(state.program, i) * 60_000 }
}

/** С последней фазы «дальше» некуда: окно закрывает только ответ на «Уснул?». */
export function next(state: WakeState, now: number): WakeState {
  const i = currentIndex(state, now)
  return i >= state.program.phases.length - 1 ? state : goTo(state, i + 1, now)
}

export function toRitual(state: WakeState, now: number): WakeState {
  return goTo(state, state.program.phases.length - 1, now)
}

export function reset(state: WakeState): WakeState {
  return { ...state, windowStartMillis: null }
}

export function formatMmSs(seconds: number): string {
  const m = Math.trunc(seconds / 60)
  const s = Math.trunc(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}
